import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EventFormModal from './EventFormModal';
import moment from 'moment';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import MobileNavigation from './components/MobileNavigation';
import MobileFooter from './components/MobileFooter';
import SeriesModal from './components/SeriesModal';
import CustomDateRangeModal from './components/CustomDateRangeModal';
import ListView from './components/ListView';
import ThreeDayView from './components/ThreeDayView';
import {
  Calendar,
  momentLocalizer,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useRef, useLayoutEffect } from 'react';
import './App.css';
import { API } from './api/api';
import { formatHours, transformTimelogsToEvents } from './utils/events';
import { getSeriesCreationState, setSeriesCreationState, addCreatedEventToSeries } from './utils/seriesState';
import { createCalendarComponents } from './components/calendar/createCalendarComponents';
import { useContextMenus } from './hooks/useContextMenus';
import { useCalendarMetrics } from './hooks/useCalendarMetrics';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import AuthenticatingModal from './components/AuthenticatingModal';


const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

moment.locale('en', {
  week: {
    dow: 1,
  },
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
  }
});

const FORMATS = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),

    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,

    selectRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} – ${localizer.format(end, 'HH:mm', culture)}`,

    agendaTimeFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),
  };

function App() {
  const [events, setEvents] = useState([]);
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [slotInfo, setSlotInfo] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '' });
  const [portalName, setPortalName] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({ enableSeries: false, demoMode: false });
  const [calendarView, setCalendarView] = useState({ showTimelogs: true, showWorktime: true });
  const [loginUrl, setLoginUrl] = useState(null);

  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      if (ev.approval_status === 'Marked' || (ev.logId && ev.logId.startsWith('temp-'))) return true;
      if (ev.isWorkTime) return calendarView.showWorktime;
      return calendarView.showTimelogs;
    });
  }, [events, calendarView]);

  const getModalPositionStyle = useCallback((date, view) => {
    const day = moment(date).day();
    let positionStyle = { right: '20px' };
    if (view === 'week') {
      if (day === 0 || day >= 4) positionStyle = { left: '20px' };
    } else if (view === 'work_week') {
      if (day >= 3) positionStyle = { left: '20px' };
    } else if (view === 'three_day') {
      const eventDate = moment(date).startOf('day');
      let rangeStart = currentDate ? moment(currentDate).startOf('day') : eventDate;
      const dayIndex = eventDate.diff(rangeStart, 'days');
      if (dayIndex === 2 || dayIndex < 0 || dayIndex > 2) {
        positionStyle = { left: '20px' };
      }
    }
    return {
      ...positionStyle,
      position: 'fixed',
      top: '80px',
      zIndex: 2600,
    };
  }, [currentDate]);

  const [modalPosition, setModalPosition] = useState(() => getModalPositionStyle(new Date(), currentView));
  const [isDragging, setIsDragging] = useState(false);

  const showError = useCallback((title, message) => {
    setErrorModalData({ title, message });
    setShowErrorModal(true);
  }, []);

  const {
    eventToCopy,
    setEventToCopy,
    contextMenu,
    setContextMenu,
    contextMenuRef,
    slotContextMenu,
    setSlotContextMenu,
    slotContextMenuRef,
    handleRightClickEvent,
    handleRightClickSlot,
  } = useContextMenus();
  const [originalEventBeforeEdit, setOriginalEventBeforeEdit] = useState(null);
  const suppressRevertRef = useRef(false);

  const [selectedSlotDate, setSelectedSlotDate] = useState(null);

  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [seriesTemplateEvent, setSeriesTemplateEvent] = useState(null);

  const [showSeriesDeleteConfirm, setShowSeriesDeleteConfirm] = useState(false);
  const [seriesPendingDelete, setSeriesPendingDelete] = useState({ seriesId: null, events: [] });
  const [isDeletingSeries, setIsDeletingSeries] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });

  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);

  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [customRangeData, setCustomRangeData] = useState(null);
  const [isLoadingCustomRange, setIsLoadingCustomRange] = useState(false);
  const [customRangeContext, setCustomRangeContext] = useState('timelogs');

  const handleCreateSeries = useCallback((templateEvent) => {
    setSeriesTemplateEvent(templateEvent);
    setShowSeriesModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, event: null });
  }, []);

  const handleCreateSeriesFromHeader = useCallback(() => {
    const defaultTemplate = {
      title: '',
      projectId: '',
      projectName: '',
      accountId: '',
      accountName: '',
      isWorkTime: false,
      billable: false,
      start: new Date(),
      end: new Date(),
    };
    setSeriesTemplateEvent(defaultTemplate);
    setShowSeriesModal(true);
  }, []);

  const handleRecoveryCleanup = useCallback(async () => {
    setIsRecovering(true);
    if (recoveryData?.createdEvents?.length) {
      for (const eventInfo of recoveryData.createdEvents) {
        try {
          await API.deleteTimelog({ logId: eventInfo.logId });
          setEvents(prev => prev.filter(ev => ev.logId !== eventInfo.logId));
        } catch (err) {
          console.error('Error cleaning up event:', eventInfo.logId, err);
        }
      }
      setSeriesCreationState(null);
    }
    setShowRecoveryModal(false);
    setRecoveryData(null);
    setIsRecovering(false);
  }, [recoveryData]);

  const handleRecoveryDismiss = useCallback(() => {
    setSeriesCreationState(null);
    setShowRecoveryModal(false);
    setRecoveryData(null);
  }, []);

  const handleCustomDateRangeClick = useCallback((context = 'timelogs') => {
    setCustomRangeContext(context);
    setShowCustomDateModal(true);
  }, []);

  const handleCustomDateRangeClose = useCallback(() => {
    setShowCustomDateModal(false);
  }, []);

  const handleClearCustomRange = useCallback(() => {
    setCustomDateRange(null);
    setCustomRangeData(null);
  }, []);

  useEffect(() => {
    const checkForIncompleteCreation = () => {
      const incompleteState = getSeriesCreationState();
      if (incompleteState) {
        const timeElapsed = Date.now() - incompleteState.startTime;
        const hoursElapsed = timeElapsed / (1000 * 60 * 60);
        setRecoveryData({
          ...incompleteState,
          hoursElapsed: Math.round(hoursElapsed * 10) / 10
        });
        setShowRecoveryModal(true);
      }
    };
    const timer = setTimeout(checkForIncompleteCreation, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const incompleteState = getSeriesCreationState();
      if (incompleteState) {
        e.preventDefault();
        e.returnValue = 'You are currently creating a series. If you leave this page, all already created events will be automatically deleted.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const setupLongPress = useCallback((handler, contextData = null) => ({
    onTouchStart: (e) => {
      const touchTimer = setTimeout(() => {
        const syntheticEvent = {
          preventDefault: () => e.preventDefault(),
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
          target: e.target,
          currentTarget: e.currentTarget
        };
        handler(contextData || e, syntheticEvent);
      }, 500);
      e.currentTarget._touchTimer = touchTimer;
    },
    onTouchEnd: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    },
    onTouchMove: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    }
  }), []);

  const handleDeleteSeries = useCallback((seriesId) => {
    if (isDeletingSeries) return;
    const seriesEvents = events.filter(event => event.seriesId === seriesId);
    if (seriesEvents.length > 0) {
      setSeriesPendingDelete({ seriesId, events: seriesEvents });
      setShowSeriesDeleteConfirm(true);
      setContextMenu({ visible: false, x: 0, y: 0, event: null });
    }
  }, [events, isDeletingSeries]);

  const handleConfirmDeleteSeries = useCallback(async () => {
    setShowSeriesDeleteConfirm(false);
    setIsDeletingSeries(true);
    setDeleteProgress({ current: 0, total: seriesPendingDelete.events.length });

    try {
      let deletedCount = 0;
      for (const event of seriesPendingDelete.events) {
        if (event.logId) {
          await API.deleteTimelog({ logId: event.logId });
        }
        deletedCount++;
        setDeleteProgress({ current: deletedCount, total: seriesPendingDelete.events.length });
      }
      setEvents(prev => prev.filter(event => event.seriesId !== seriesPendingDelete.seriesId));
      setSeriesPendingDelete({ seriesId: null, events: [] });
      setIsDeletingSeries(false);
      setDeleteProgress({ current: 0, total: 0 });
    } catch (err) {
      console.error('Error deleting series:', err);
      setIsDeletingSeries(false);
      setDeleteProgress({ current: 0, total: 0 });
      showError('Delete Failed', 'Failed to delete some events in the series. Please try again.');
    }
  }, [seriesPendingDelete, showError]);

  const handleBatchCreateSeries = useCallback(async (eventsToCreate, setProgress) => {
    const seriesId = eventsToCreate[0]?.seriesId;
    if (!seriesId) throw new Error('Series ID is required');

    const seriesState = {
      seriesId,
      totalEvents: eventsToCreate.length,
      createdEvents: [],
      startTime: Date.now(),
      templateData: {
        projectId: eventsToCreate[0]?.projectId,
        title: eventsToCreate[0]?.title,
      }
    };
    setSeriesCreationState(seriesState);

    try {
      for (let i = 0; i < eventsToCreate.length; i++) {
        const eventData = eventsToCreate[i];
        setProgress({ current: i, total: eventsToCreate.length });

        const data = await API.createTimelog({
          start: eventData.start,
          end: eventData.end,
          name: eventData.isWorkTime ? 'WorkTime' : (eventData.title || ''),
          projectId: eventData.isWorkTime ? '' : (eventData.projectId || ''),
          accountId: eventData.isWorkTime ? '' : (eventData.accountId || ''),
          billable: eventData.isWorkTime ? false : (eventData.billable || false),
          isWorkTime: eventData.isWorkTime || false,
        });

        if (data.success) {
          const savedEvent = {
            ...data.data,
            start: new Date(data.data.start),
            end: new Date(data.data.end),
            seriesId: eventData.seriesId,
          };
          setEvents((prev) => [...prev, savedEvent]);
          addCreatedEventToSeries(data.data.logId, '', '');
        } else {
          throw new Error('Failed to save event');
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setProgress({ current: eventsToCreate.length, total: eventsToCreate.length });
      setSeriesCreationState(null);
    } catch (err) {
      console.error('Error creating series:', err);
      const currentState = getSeriesCreationState();
      if (currentState?.createdEvents?.length) {
        for (const eventInfo of currentState.createdEvents) {
          try {
            await API.deleteTimelog({ logId: eventInfo.logId });
            setEvents(prev => prev.filter(ev => ev.logId !== eventInfo.logId));
          } catch (rollbackErr) {
            console.error('Error rolling back event:', eventInfo.logId, rollbackErr);
          }
        }
        setSeriesCreationState(null);
      }
      throw err;
    }
  }, []);

  const createMarkedPlaceholder = useCallback((start, end, allDay) => {
    const tempId = `temp-${Date.now()}`;
    const base = {
      title: '',
      start,
      end,
      logId: tempId,
      approval_status: 'Marked',
      projectId: '',
      projectName: '',
      accountId: '',
      accountName: '',
    };
    return allDay === undefined ? base : { ...base, allDay };
  }, []);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const data = await API.refreshTimelogs();
      setEvents(transformTimelogsToEvents(data.data || []));
    } catch {
      // silently ignore
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const handleAddNew = useCallback(() => {
    const now = (currentView === 'day' || currentView === 'three_day') ? new Date(currentDate) : new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0, 0, 0);
    } else {
      now.setMinutes(roundedMinutes, 0, 0);
    }
    const start = new Date(now);
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    const placeholder = createMarkedPlaceholder(start, end);
    setEvents(prev => [
      ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
      placeholder
    ]);
    setSlotInfo({ start, end, tempId: placeholder.logId });
    setModalPosition(getModalPositionStyle(start, currentView));
    setShowModal(true);
  }, [currentView, currentDate, setEvents, setSlotInfo, setModalPosition, setShowModal, getModalPositionStyle, createMarkedPlaceholder]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === 'n' &&
        !showModal &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA'
      ) {
        handleAddNew();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, handleAddNew]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu({ visible: false, x: 0, y: 0, event: null });
      }
      if (slotContextMenuRef.current && !slotContextMenuRef.current.contains(event.target)) {
        setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
      }
    };
    if (contextMenu.visible || slotContextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible, slotContextMenu.visible]);

  const isDraggable = useCallback((event) => event.approval_status !== 'Approved', []);
  const isResizable = useCallback((event) => event.approval_status !== 'Approved', []);

  const calendarRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const previousViewRef = useRef(currentView);
  const previousDateRef = useRef(currentDate);
  const previousAuthRef = useRef(authenticated);

  const scrollToTime = useCallback((targetTime, smooth = false) => {
    if (!calendarRef.current) return;
    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');
    if (!scrollContainer) return;

    const targetHour = typeof targetTime === 'number' ? targetTime : targetTime.getHours();
    const targetMinutes = typeof targetTime === 'number' ? 0 : targetTime.getMinutes();
    const totalMinutes = targetHour * 60 + targetMinutes;

    const timeSlots = scrollContainer.querySelectorAll('.rbc-time-slot');
    let pixelsPerHour = 40;
    if (timeSlots.length > 0) {
      const slotHeight = timeSlots[0].offsetHeight;
      pixelsPerHour = slotHeight * 2;
    }

    const paddingHours = 1;
    const scrollPosition = Math.max(0, (totalMinutes / 60 - paddingHours) * pixelsPerHour);

    if (smooth) {
      scrollContainer.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    } else {
      scrollContainer.scrollTop = scrollPosition;
    }
  }, []);

  useLayoutEffect(() => {
    if (!calendarRef.current || showModal) return;
    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');
    if (scrollContainer) {
      const viewChanged = previousViewRef.current !== currentView;
      const dateChanged = previousDateRef.current !== currentDate;
      const authChanged = previousAuthRef.current !== authenticated;

      if (!hasAutoScrolledRef.current || viewChanged || dateChanged || authChanged) {
        const now = new Date();
        const currentHour = now.getHours();
        let targetHour = Math.max(6, currentHour - 2);
        if (currentHour < 8) targetHour = 6;
        scrollToTime(targetHour, false);
        hasAutoScrolledRef.current = true;
      }

      previousViewRef.current = currentView;
      previousDateRef.current = currentDate;
      previousAuthRef.current = authenticated;
    }
  }, [currentView, showModal, currentDate, scrollToTime, authenticated]);

  useAuthBootstrap({
    authenticated,
    setAuthenticated,
    setPortalName,
    setEvents,
    showError,
    setError,
    setCurrentView,
    setFeatureFlags,
    setLoginUrl,
  });

  useEffect(() => {
    if (authenticated && currentView) {
      API.saveUserViewPreference(currentView).catch(() => {});
    }
  }, [currentView, authenticated]);

  const eventPropGetter = useCallback((event) => {
    let backgroundColor = '#BDD9F5';
    let borderStyle = 'solid';
    let borderColor = '#205799';

    if (event.isWorkTime) {
      backgroundColor = '#FFCCA0';
      borderColor = '#FF7800';
    } else if (event.approval_status === 'Approved') {
      backgroundColor = event.billable ? '#EDF3F6' : '#EEEAE6';
    } else if (event.approval_status === 'Rejected' || event.approval_status === 'Changed') {
      backgroundColor = '#767279';
    }

    if (event.approval_status === 'Marked') {
      backgroundColor = 'rgba(32, 87, 153, 0.08)';
      borderStyle = 'dashed';
      borderColor = '#205799';
    } else if (editingEvent?.logId === event.logId) {
      backgroundColor = '#EDF3F6';
      borderStyle = 'solid';
      borderColor = '#205799';
    }

    return {
      style: {
        backgroundColor,
        color: event.approval_status === 'Rejected' ? 'white' : 'black',
        borderRadius: '4px',
        border: `1px ${borderStyle} ${borderColor}`,
        padding: '2px 4px',
        boxShadow: event.approval_status === 'Marked'
          ? '0 0 0 2px rgba(32, 87, 153, 0.15)'
          : '0 0 10px 0 rgba(0, 0, 0, 0.2)',
        opacity: event.approval_status === 'Marked' ? 0.95 : 1,
      },
    };
  }, [editingEvent]);

  const {
    totalsCurrentView,
    totalsCurrentViewPeople,
    totalsYTD,
    totalsYTDPeople,
    getDailyTotals,
    getCustomPeriodTotals,
    getYearOverYearComparison,
  } = useCalendarMetrics({ events, currentDate, currentView, enablePeople: false });

  const handleCustomDateRangeApply = useCallback((range) => {
    setIsLoadingCustomRange(true);
    setCustomDateRange({ ...range, context: customRangeContext });

    try {
      if (range.compareWithPreviousYear) {
        const comparisonData = getYearOverYearComparison(range.start, range.end, customRangeContext);
        setCustomRangeData({
          ...comparisonData,
          range: { ...range, context: customRangeContext },
          label: range.label || 'Custom Range',
        });
      } else {
        const currentPeriod = getCustomPeriodTotals(range.start, range.end, customRangeContext);
        setCustomRangeData({
          current: currentPeriod,
          range: { ...range, context: customRangeContext },
          label: range.label || 'Custom Range',
          hasData: currentPeriod.total > 0,
        });
      }
    } catch (err) {
      console.error('Error calculating custom range data:', err);
      showError('Calculation Error', 'An error occurred while calculating the selected period');
    } finally {
      setIsLoadingCustomRange(false);
    }
  }, [customRangeContext, showError, getCustomPeriodTotals, getYearOverYearComparison]);

  const handleSaveEvent = useCallback((newEvent) => {
    if (newEvent.isWorkTime) {
      const s = new Date(newEvent.start), e = new Date(newEvent.end);
      const overlap = events.find(ev => ev.isWorkTime && s < new Date(ev.end) && e > new Date(ev.start));
      if (overlap) {
        showError('Overlap', 'A WorkTime entry already exists in this time range.');
        return;
      }
    }
    API.createTimelog({
      start: newEvent.start,
      end: newEvent.end,
      name: newEvent.isWorkTime ? 'WorkTime' : (newEvent.name || newEvent.title || ''),
      projectId: newEvent.isWorkTime ? '' : (newEvent.projectId || ''),
      accountId: newEvent.isWorkTime ? '' : (newEvent.accountId || ''),
      projectName: newEvent.isWorkTime ? '' : (newEvent.projectName || ''),
      accountName: newEvent.isWorkTime ? '' : (newEvent.accountName || ''),
      billable: newEvent.isWorkTime ? false : (newEvent.billable || false),
      isWorkTime: newEvent.isWorkTime || false,
      worktypeId: newEvent.worktypeId || '',
    })
      .then((data) => {
        if (data.success) {
          const savedEvent = {
            ...data.data,
            start: new Date(data.data.start),
            end: new Date(data.data.end),
          };
          setEvents(prev => [
            ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId?.startsWith('temp-'))),
            savedEvent,
          ]);
        } else {
          showError('Save Failed', 'Failed to save event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while saving event. Please check your connection and try again.');
      });
  }, [showError, events]);

  const handleUpdateEvent = useCallback((updatedEvent) => {
    if (updatedEvent.isWorkTime) {
      const s = new Date(updatedEvent.start), e = new Date(updatedEvent.end);
      const overlap = events.find(ev => ev.isWorkTime && ev.logId !== updatedEvent.logId && s < new Date(ev.end) && e > new Date(ev.start));
      if (overlap) {
        showError('Overlap', 'A WorkTime entry already exists in this time range.');
        return;
      }
    }
    API.updateTimelog({
      start: updatedEvent.start,
      end: updatedEvent.end,
      name: updatedEvent.isWorkTime ? 'WorkTime' : (updatedEvent.name || updatedEvent.title || ''),
      projectId: updatedEvent.isWorkTime ? '' : (updatedEvent.projectId || ''),
      accountId: updatedEvent.isWorkTime ? '' : (updatedEvent.accountId || ''),
      projectName: updatedEvent.isWorkTime ? '' : (updatedEvent.projectName || ''),
      accountName: updatedEvent.isWorkTime ? '' : (updatedEvent.accountName || ''),
      billable: updatedEvent.isWorkTime ? false : (updatedEvent.billable || false),
      isWorkTime: updatedEvent.isWorkTime || false,
      worktypeId: updatedEvent.worktypeId || '',
      logId: updatedEvent.logId,
    })
      .then((data) => {
        if (data.success) {
          const savedEvent = {
            ...data.data,
            start: new Date(data.data.start),
            end: new Date(data.data.end),
          };
          setEvents(prev => prev.map(ev => ev.logId === updatedEvent.logId ? savedEvent : ev));
          setOriginalEventBeforeEdit(null);
        } else {
          showError('Update Failed', 'Failed to update event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while updating event. Please check your connection and try again.');
      });
  }, [showError, events]);

  const handleDeleteEvent = useCallback((logId) => {
    API.deleteTimelog({ logId })
      .then((data) => {
        if (data.success) {
          setEvents(prev => prev.filter(ev => ev.logId !== logId));
        } else {
          showError('Delete Failed', 'Failed to delete event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while deleting event. Please check your connection and try again.');
      });
  }, [showError]);

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const max = useMemo(() => new Date(2025, 4, 10, 23, 59), []);
  const timeslots = 2;
  const views = {
    month: true,
    week: true,
    work_week: true,
    three_day: ThreeDayView,
    day: true,
    list: true
  };

  const handleDayClick = useCallback((date) => {
    setCurrentDate(date);
    setCurrentView('day');
  }, []);

  const handleSelectSlot = useCallback((slotInfo, e) => {
    if (currentView === 'month') {
      handleDayClick(slotInfo.start);
      return;
    }
    if (e && e.type === 'contextmenu') {
      handleRightClickSlot(slotInfo, e);
      return;
    }
    if (showModal) return;

    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setEditingEvent(null);
    setIsDragging(false);
    setSelectedSlotDate(slotInfo.start);
    setModalPosition(getModalPositionStyle(slotInfo.start, currentView));

    if (eventToCopy) {
      const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
      const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
      const copiedEvent = { ...eventToCopy, start: slotInfo.start, end: newEnd };
      handleSaveEvent(copiedEvent);
      setEventToCopy(null);
    } else {
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start: slotInfo.start,
        end: slotInfo.end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        projectName: '',
        accountId: '',
        accountName: '',
      };
      setEvents(prev => [
        ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
        placeholder
      ]);
      setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId });
      setShowModal(true);
    }
  }, [eventToCopy, handleSaveEvent, currentView, originalEventBeforeEdit, handleDayClick, handleRightClickSlot, showModal]);

  const handleSelectEvent = useCallback((event) => {
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setIsDragging(false);
    setShowDeleteConfirm(false);
    setEventPendingDelete(null);
    setOriginalEventBeforeEdit(event);

    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    setShowModal(true);
  }, [originalEventBeforeEdit]);

  const handleModalTimeChange = useCallback((updatedEvent) => {
    setEvents((prevEvents) =>
      prevEvents.map(ev =>
        (updatedEvent.logId && ev.logId === updatedEvent.logId) ||
        (!updatedEvent.logId && slotInfo?.tempId && ev.logId === slotInfo.tempId)
          ? { ...ev, start: updatedEvent.start, end: updatedEvent.end }
          : ev
      )
    );
    setEditingEvent((prev) =>
      prev && prev.logId === updatedEvent.logId
        ? { ...prev, start: updatedEvent.start, end: updatedEvent.end }
        : prev
    );
    if (!updatedEvent.logId && slotInfo) {
      setSlotInfo((prev) => prev ? { ...prev, start: updatedEvent.start, end: updatedEvent.end } : prev);
    }
    if (updatedEvent.start && (currentView === 'day' || currentView === 'three_day' || currentView === 'week' || currentView === 'work_week')) {
      scrollToTime(updatedEvent.start, true);
    }
  }, [slotInfo, currentView, scrollToTime]);

  const handleEventDrop = useCallback(({ event, start, end, allDay }) => {
    setIsDragging(false);
    setModalPosition(getModalPositionStyle(start, currentView));
    setOriginalEventBeforeEdit(prev => prev ?? event);

    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }

    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '', start, end, allDay,
        logId: tempId, approval_status: 'Marked',
        projectId: '', projectName: '', accountId: '', accountName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }
    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    const updated = { ...event, start, end, allDay, approval_status: 'Changed' };
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) => prev.map((ev) => (ev === event ? updated : ev)));
  }, [currentView]);

  const handleEventResize = useCallback(({ event, start, end }) => {
    setIsDragging(false);
    setOriginalEventBeforeEdit(prev => prev ?? event);

    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }

    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '', start, end, logId: tempId, approval_status: 'Marked',
        projectId: '', projectName: '', accountId: '', accountName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }

    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    const updated = { ...event, start, end, approval_status: 'Changed' };
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) => prev.map((ev) => (ev === event ? updated : ev)));
  }, []);

  const calendarComponents = useMemo(() => createCalendarComponents({
    currentView,
    formatHours,
    getDailyTotals,
    handleDayClick,
    handleRightClickEvent,
    setupLongPress,
  }), [currentView, getDailyTotals, handleDayClick, handleRightClickEvent, setupLongPress]);

  if (error && (error.includes('Error 1') || error.includes('Error 2') || error.includes('Error 3'))) {
    let title, message, details;
    if (error.includes('Error 1')) {
      title = 'Authentication Required';
      message = 'Unable to verify your access credentials.';
      details = 'The authentication parameter is missing from your URL.';
    } else if (error.includes('Error 2')) {
      title = 'Login Failed';
      message = 'Authentication could not be completed.';
      details = 'Your login credentials could not be verified with the server.';
    } else if (error.includes('Error 3')) {
      title = 'Connection Error';
      message = 'Unable to connect to the authentication server.';
      details = 'Please check your internet connection and try again.';
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500 mb-6">{details}</p>
            <div className="bg-[#EDF3F6] rounded-lg p-4">
              <p className="text-sm text-white">
                If the issue persists, please contact the IT department.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    if (loginUrl) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Login Required</h1>
              <p className="text-gray-600 mb-6">
                Your browser blocked the login popup. Click the button below to open it.
              </p>
              <button
                onClick={() => window.open(loginUrl, 'oauthPopup', 'width=600,height=700')}
                className="w-full bg-[#205799] hover:bg-[#273C61] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Open Login Window
              </button>
            </div>
          </div>
        </div>
      );
    }
    return <AuthenticatingModal isOpen={true} />;
  }

  return (
    <div ref={calendarRef} className="h-screen flex flex-col bg-white app-container">
      <MobileNavigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentDate={currentDate}
        handleNavigate={handleNavigate}
        onAddNew={handleAddNew}
        onCreateSeries={handleCreateSeriesFromHeader}
        enableSeries={featureFlags.enableSeries}
        calendarView={calendarView}
        setCalendarView={setCalendarView}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <div
        className="flex-1 calendar-container-mobile md:calendar-container-tablet lg:calendar-container-desktop"
        onContextMenu={(e) => {
          const target = e.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');

          if (!isEvent && isSlot && currentView !== 'month') {
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = e.clientY - columnRect.top;

              const timeView = e.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;

              let startHour = 0;
              let endHour = 24;
              let totalHours = 24;

              if (timeGutter) {
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) startHour = parseInt(match[1]);
                  }
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) endHour = parseInt(match[1]) + 1;
                  }
                  totalHours = endHour - startHour;
                }
              }

              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;

              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);

              if (currentView === 'day' || currentView === 'three_day') {
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                const headers = e.currentTarget.querySelectorAll('.rbc-header');
                let dayIndex = 0;
                if (headers.length > 0) {
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (e.clientX >= headerRect.left && e.clientX <= headerRect.right) {
                      dayIndex = i;
                      break;
                    }
                  }
                }
                const startOfWeek = moment(currentDate).startOf('isoWeek');
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              }

              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
              handleRightClickSlot({ start: slotStart, end: slotEnd }, e);
            }
          }
        }}
        {...setupLongPress((e, syntheticEvent) => {
          const target = syntheticEvent.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');

          if (!isEvent && isSlot && currentView !== 'month') {
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = syntheticEvent.clientY - columnRect.top;

              const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;

              let startHour = 0;
              let endHour = 24;
              let totalHours = 24;

              if (timeGutter) {
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) startHour = parseInt(match[1]);
                  }
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) endHour = parseInt(match[1]) + 1;
                  }
                  totalHours = endHour - startHour;
                }
              }

              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;

              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);

              if (currentView === 'day' || currentView === 'three_day') {
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                const headers = syntheticEvent.currentTarget.querySelectorAll('.rbc-header');
                let dayIndex = 0;
                if (headers.length > 0) {
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (syntheticEvent.clientX >= headerRect.left && syntheticEvent.clientX <= headerRect.right) {
                      dayIndex = i;
                      break;
                    }
                  }
                }
                const startOfWeek = moment(currentDate).startOf('isoWeek');
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              }

              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
              handleRightClickSlot({ start: slotStart, end: slotEnd }, syntheticEvent);
            }
          }
        })}
      >
        {currentView === 'list' ? (
          <ListView
            events={filteredEvents}
            currentDate={currentDate}
            onSelectEvent={handleSelectEvent}
            onRightClickEvent={handleRightClickEvent}
            setupLongPress={setupLongPress}
          />
        ) : (
          <DnDCalendar
            events={filteredEvents}
            onDragStart={() => setIsDragging(true)}
            localizer={localizer}
            date={currentDate}
            onNavigate={handleNavigate}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            onView={setCurrentView}
            views={views}
            max={max}
            step={30}
            snapToSlot={false}
            timeslots={timeslots}
            showMultiDayTimes
            selectable
            popup
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            resizable
            formats={FORMATS}
            eventPropGetter={eventPropGetter}
            draggableAccessor={isDraggable}
            resizableAccessor={isResizable}
            components={calendarComponents}
          />
        )}
      </div>

      <MobileFooter
        totalsCurrentView={totalsCurrentView}
        totalsCurrentViewPeople={totalsCurrentViewPeople}
        totalsYTD={totalsYTD}
        currentView={currentView}
        currentDate={currentDate}
        customRangeData={customRangeData}
        isLoadingCustomRange={isLoadingCustomRange}
        onCustomDateRangeClick={handleCustomDateRangeClick}
        onClearCustomRange={handleClearCustomRange}
        enablePeople={false}
        totalsYTDPeople={totalsYTDPeople}
      />

      {contextMenu.visible && (
        <ul
          ref={contextMenuRef}
          className="absolute bg-white border border-gray-200 shadow-lg rounded-md p-2 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              setEventToCopy(contextMenu.event);
              setContextMenu({ visible: false, x: 0, y: 0, event: null });
            }}
          >
            Copy
          </li>
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => handleCreateSeries(contextMenu.event)}
          >
            Create Series
          </li>
          {contextMenu.event?.seriesId && (
            <li
              className="px-2 py-1 cursor-pointer text-orange-600 hover:bg-gray-100"
              onClick={() => handleDeleteSeries(contextMenu.event.seriesId)}
            >
              Delete Entire Series
            </li>
          )}
          {contextMenu.event?.approval_status !== 'Approved' && (
            <>
              <li
                className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setEditingEvent(contextMenu.event);
                  setShowModal(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                Edit
              </li>
              <li
                className="px-2 py-1 cursor-pointer text-red-600 hover:bg-gray-100"
                onClick={() => {
                  setEventPendingDelete(contextMenu.event);
                  setShowDeleteConfirm(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                Delete
              </li>
            </>
          )}
        </ul>
      )}

      {slotContextMenu.visible && (
        <ul
          ref={slotContextMenuRef}
          className="absolute bg-white border border-gray-200 shadow-lg rounded-md p-2 z-50"
          style={{ top: slotContextMenu.y, left: slotContextMenu.x }}
        >
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              const slot = slotContextMenu.slot;
              const placeholder = createMarkedPlaceholder(slot.start, slot.end);
              setEvents(prev => [
                ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
                placeholder
              ]);
              setSlotInfo({ start: slot.start, end: slot.end, tempId: placeholder.logId });
              setModalPosition(getModalPositionStyle(slot.start, currentView));
              setShowModal(true);
              setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
            }}
          >
            Add New
          </li>
          {eventToCopy && (
            <li
              className="px-2 py-1 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                const slot = slotContextMenu.slot;
                const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
                const newEnd = moment(slot.start).add(duration, 'ms').toDate();
                handleSaveEvent({ ...eventToCopy, start: slot.start, end: newEnd });
                setEventToCopy(null);
                setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
              }}
            >
              Paste
            </li>
          )}
        </ul>
      )}

      {!isDragging && (
        <EventFormModal
          onTimeChange={handleModalTimeChange}
          onNavigate={handleNavigate}
          currentDate={currentDate}
          key={editingEvent?.logId || slotInfo?.tempId || 'new'}
          modalPosition={modalPosition}
          setModalPosition={setModalPosition}
          getModalPositionStyle={getModalPositionStyle}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
            if (slotInfo?.tempId) {
              setEvents(prev => prev.filter(ev => ev.logId !== slotInfo.tempId));
            }
            setSlotInfo(null);
            if (!suppressRevertRef.current && originalEventBeforeEdit) {
              setEvents(prev =>
                prev.map(ev =>
                  ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
                )
              );
            }
            setOriginalEventBeforeEdit(null);
            suppressRevertRef.current = false;
          }}
          onSave={(eventData) => {
            suppressRevertRef.current = true;
            if (editingEvent) {
              handleUpdateEvent({ ...eventData, logId: editingEvent.logId });
            } else {
              handleSaveEvent(eventData);
            }
            setShowModal(false);
            setEditingEvent(null);
            setSlotInfo(null);
            setOriginalEventBeforeEdit(null);
          }}
          onDelete={handleDeleteEvent}
          start={editingEvent?.start || slotInfo?.start}
          end={editingEvent?.end || slotInfo?.end}
          initialData={editingEvent}
          selectedSlotDate={selectedSlotDate}
          currentView={currentView}
        />
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={() => {
          if (eventPendingDelete) {
            handleDeleteEvent(eventPendingDelete.logId);
            setEventPendingDelete(null);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => {
          setEventPendingDelete(null);
          setShowDeleteConfirm(false);
        }}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      <ConfirmModal
        isOpen={showSeriesDeleteConfirm}
        onConfirm={handleConfirmDeleteSeries}
        onCancel={() => {
          setSeriesPendingDelete({ seriesId: null, events: [] });
          setShowSeriesDeleteConfirm(false);
        }}
        title="Delete Entire Series"
        message={`Are you sure you want to delete ${seriesPendingDelete.events.length} time registration${seriesPendingDelete.events.length !== 1 ? 's' : ''} from this series? This action cannot be undone.`}
        confirmText={`Delete ${seriesPendingDelete.events.length} Registration${seriesPendingDelete.events.length !== 1 ? 's' : ''}`}
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
      />

      {isDeletingSeries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div
                  className="animate-spin rounded-full h-8 w-8 border-b-2 mr-3"
                  style={{ borderColor: '#205799' }}
                ></div>
                <div className="text-lg font-semibold text-gray-900">Deleting Series</div>
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Deleting {deleteProgress.current} of {deleteProgress.total} registrations...
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${(deleteProgress.current / deleteProgress.total) * 100}%`,
                    backgroundColor: '#205799'
                  }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Please wait while we delete all entries...
              </div>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
      />

      {showRecoveryModal && recoveryData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900 mb-4">
                Incomplete Series Found
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Found an incomplete series creation from {recoveryData.hoursElapsed < 1 ?
                  `${Math.round(recoveryData.hoursElapsed * 60)} minutes` :
                  `${recoveryData.hoursElapsed} hours`} ago:
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
                <div className="text-sm">
                  <strong>Series:</strong> {recoveryData.templateData.title}<br/>
                  <strong>Created:</strong> {recoveryData.createdEvents.length} of {recoveryData.totalEvents} events
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-6">
                What would you like to do with the already created time registrations?
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleRecoveryCleanup}
                  disabled={isRecovering}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  {isRecovering ? 'Deleting...' : 'Delete All'}
                </button>
                <button
                  onClick={handleRecoveryDismiss}
                  disabled={isRecovering}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  Keep Events
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {featureFlags.enableSeries && (
        <SeriesModal
          isOpen={showSeriesModal}
          onClose={() => {
            setShowSeriesModal(false);
            setSeriesTemplateEvent(null);
          }}
          onCreateSeries={handleBatchCreateSeries}
          templateEvent={seriesTemplateEvent}
          projects={[]}
        />
      )}

      <CustomDateRangeModal
        isOpen={showCustomDateModal}
        onClose={handleCustomDateRangeClose}
        onApply={handleCustomDateRangeApply}
        currentRange={customDateRange}
        isPeopleContext={false}
      />
    </div>
  );
}

export default App;
